import InterWebviewBridge, {
  IWbContractWalletAndroid,
  IWbContractWalletIOS,
  IWbWalletTransactionType,
  interWbWallet,
} from '@interco/inter-webview-bridge'

import { IWbOpenWalletAndroid, IWbOpenWalletIOS } from './types'
import NewRelicUtils from '../monitoring/NewRelicUtils'
import BaseBridge from './BaseBridge'

export interface ContractWalletResult {
  transactionId: string
  status: string
}

export interface ChosenPaymentResult {
  title: string
  subTitle: string
  type: string
}

export class CheckoutBaseBridge {
  private static openWalletResponse?: IWbOpenWalletAndroid | IWbOpenWalletIOS

  private static mapOpenWalletResponse(
    response: IWbOpenWalletAndroid | IWbOpenWalletIOS,
  ): ChosenPaymentResult {
    CheckoutBaseBridge.openWalletResponse = response

    const paymentMethodTitle =
      (response as IWbOpenWalletAndroid)?.selectedPaymentMethod?.paymentMethodTitle ||
      (response as IWbOpenWalletIOS)?.method?.title

    const paymentMethod =
      (response as IWbOpenWalletAndroid)?.selectedPaymentMethod?.type ||
      (response as IWbOpenWalletIOS)?.method?.type

    const paymentMethodInfo =
      (response as IWbOpenWalletAndroid)?.selectedPaymentMethod?.firstLine ||
      (response as IWbOpenWalletIOS)?.method?.firstLine

    return {
      title: paymentMethodTitle,
      subTitle: paymentMethodInfo || '',
      type: paymentMethod,
    }
  }

  /**
   * Método para invocar a forma de pagamento padrão.
   * @param amount valor em centavos.
   * @param walletClientId tipo de transação.
   * @param additionalFields campo contendo informações adicionais, chave|valor.
   * @param additionalQueryRequestParams campo contendo informações adicionais, chave|valor.
   * @returns informações referentes ao pagamento padrão.
   */
  static async defaultPaymentMethod(
    amount: string,
    walletClientId: 'CAPITALIZATION',
    additionalFields: Record<string, string>,
    additionalQueryRequestParams: Record<string, string>,
  ): Promise<ChosenPaymentResult> {
    if (BaseBridge.isBrowser())
      return {
        title: `Crédito Inter`,
        subTitle: 'Não cobrado em ambiente de desenvolvimento.',
        type: 'CREDIT',
      } as ChosenPaymentResult

    const price = Number.parseFloat(amount) * 100

    const priceFixed = `${price.toFixed(0)}`

    try {
      const iWb = InterWebviewBridge.getInstance()

      const response: IWbOpenWalletAndroid | IWbOpenWalletIOS = await iWb.execute({
        action: 'getPaymentDefault',
        metadata: {
          walletClientId,
          amount: priceFixed,
          additionalFields,
          additionalQueryRequestParams,
          paymentMethodType: 'DEBIT',
        },
      })

      return CheckoutBaseBridge.mapOpenWalletResponse(response)
    } catch (error) {
      NewRelicUtils.noticeError(error as Error, {
        errorCodeRef: 'CheckoutBaseService.paymentDefault',
      })

      return Promise.reject(error)
    }
  }

  /**
   * Método para invocar a seleção de formas de pagamento.
   * @param valor em centavos.
   * @param walletClientId tipo de transação.
   * @param additionalFields campo contendo informações adicionais, chave|valor.
   * @param additionalQueryRequestParams campo contendo informações adicionais, chave|valor.
   * @returns informações referentes ao método de pagamento selecionado.
   */
  static async openWallet(
    amount: string,
    walletClientId: 'CAPITALIZATION',
    additionalFields: Record<string, string>,
    additionalQueryRequestParams: Record<string, string>,
  ): Promise<ChosenPaymentResult> {
    if (BaseBridge.isBrowser())
      return {
        title: `Crédito Inter`,
        subTitle: 'Não cobrado em ambiente de desenvolvimento.',
        type: 'CREDIT',
      } as ChosenPaymentResult

    const price = Number.parseFloat(amount) * 100

    try {
      const response = await interWbWallet.openWallet(
        walletClientId,
        `${price.toFixed(0)}`,
        additionalFields,
        additionalQueryRequestParams,
      )

      return CheckoutBaseBridge.mapOpenWalletResponse(response)
    } catch (error) {
      NewRelicUtils.noticeError(error as Error, {
        errorCodeRef: 'CheckoutBaseService.openWallet',
      })

      return Promise.reject(error)
    }
  }

  /**
   * Método invocado para finalizar a contratação via wallet.
   * @param amount valor da transação em reais.
   * @returns o status e o id da transação de contratação.
   */
  static async contractWallet(amount: string): Promise<ContractWalletResult> {
    if (BaseBridge.isBrowser()) {
      return Promise.resolve({
        transactionId: '',
        status: 'SUCCESS',
      } as ContractWalletResult)
    }

    if (!this.openWalletResponse) throw new Error('Erro ao resgatar os dados da forma de pagamento')

    const price = Number.parseFloat(amount)

    const transactionType: IWbWalletTransactionType = {
      code: '16',
      checkType: 5,
      description: 'AUTORIZACAO_SEGURO',
      value: price,
    }

    try {
      const result = await interWbWallet.contractWallet(this.openWalletResponse, transactionType)

      if (result.status === 'FAILURE') throw new Error(result.error)

      const transactionId =
        (result as IWbContractWalletAndroid)?.walletLibraryCheckoutResult?.walletClientId ||
        (result as IWbContractWalletIOS)?.response?.clientTransactionId

      return {
        transactionId,
        status: result.status,
      }
    } catch (error) {
      const errorMessage = error as Error

      if (errorMessage.message !== 'walletCheckoutManagerDidUserCancelISafeAuthentication') {
        NewRelicUtils.noticeError(errorMessage, {
          errorCodeRef: 'CheckoutBaseService.contractWallet',
        })
      }

      return Promise.reject(error)
    }
  }
}
